import { useState } from 'react';
import { Button, Select, Input, Modal, Tag, Space, Alert, Tooltip } from 'antd';
import { Calculator, Plus, X } from 'lucide-react';

/**
 * Computed Expression Builder — UI giúp admin tạo công thức tính toán
 * mà không cần biết syntax. User chọn:
 * - Biến nguồn (tên field/column)
 * - Operator (+, -, *, /, max, min)
 * - Có thể nhóm bằng dấu ngoặc
 *
 * Output: string biểu thức như "max(0, tong_cong - tam_ung)" hoặc "so_luong * don_gia"
 *
 * Có 2 mode:
 * - Simple: a OP b (đa số case dùng được)
 * - Advanced: textarea cho power user
 */
interface ExpressionBuilderProps {
  value?: string;
  onChange: (expr: string | undefined) => void;
  /** List các biến có thể tham chiếu trong context này. VD ['so_luong', 'don_gia', 'tong_cong'] */
  availableVars: string[];
  /** Label cho nút trigger */
  triggerLabel?: string;
}

const OPERATORS = [
  { value: '+', label: '+ (cộng)' },
  { value: '-', label: '− (trừ)' },
  { value: '*', label: '× (nhân)' },
  { value: '/', label: '÷ (chia)' },
];

const FUNCS = [
  { value: 'max', label: 'max — không bao giờ âm' },
  { value: 'min', label: 'min — không bao giờ vượt' },
  { value: 'round', label: 'round — làm tròn' },
  { value: 'floor', label: 'floor — làm tròn xuống' },
  { value: 'ceil', label: 'ceil — làm tròn lên' },
  { value: 'abs', label: 'abs — giá trị tuyệt đối' },
];

export function ExpressionBuilder({
  value, onChange, availableVars, triggerLabel = 'Mở',
}: ExpressionBuilderProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [mode, setMode] = useState<'simple' | 'function' | 'advanced'>('simple');

  // Simple mode state
  const [opLeft, setOpLeft] = useState<string>('');
  const [opType, setOpType] = useState<string>('*');
  const [opRight, setOpRight] = useState<string>('');

  // Function mode state (max/min)
  const [funcName, setFuncName] = useState<string>('max');
  const [funcArg1, setFuncArg1] = useState<string>('0');
  const [funcArg2, setFuncArg2] = useState<string>('');

  const openBuilder = () => {
    setDraft(value ?? '');
    setOpen(true);
  };

  const buildSimple = () => {
    if (!opLeft || !opRight) {
      return;
    }
    setDraft(`${opLeft} ${opType} ${opRight}`);
  };

  const buildFunction = () => {
    if (!funcArg1 || !funcArg2) return;
    setDraft(`${funcName}(${funcArg1}, ${funcArg2})`);
  };

  const apply = () => {
    onChange(draft.trim() || undefined);
    setOpen(false);
  };

  const clear = () => {
    setDraft('');
    onChange(undefined);
    setOpen(false);
  };

  return (
    <>
      <Space style={{ width: '100%' }} size={4}>
        <Input
          size="small"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          placeholder="(không tính tự động)"
          style={{ flex: 1, fontFamily: 'monospace', fontSize: 11 }}
          readOnly
        />
        <Button size="small" icon={<Calculator size={12} />} onClick={openBuilder}>
          {triggerLabel}
        </Button>
        {value && (
          <Button size="small" icon={<X size={12} />} danger type="text"
            onClick={() => onChange(undefined)} />
        )}
      </Space>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Tạo công thức tính toán"
        width={600}
        onOk={apply}
        okText="Áp dụng"
        cancelText="Hủy"
        footer={[
          <Button key="clear" danger onClick={clear}>Xóa công thức</Button>,
          <Button key="cancel" onClick={() => setOpen(false)}>Hủy</Button>,
          <Button key="ok" type="primary" onClick={apply}>Áp dụng</Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Công thức cho phép cộng/trừ/nhân/chia các biến nguồn — kết quả tự cập nhật khi user nhập"
          />

          <Tag.CheckableTag checked={mode === 'simple'} onChange={() => setMode('simple')}>
            Đơn giản (a × b)
          </Tag.CheckableTag>
          <Tag.CheckableTag checked={mode === 'function'} onChange={() => setMode('function')}>
            Hàm (max, min...)
          </Tag.CheckableTag>
          <Tag.CheckableTag checked={mode === 'advanced'} onChange={() => setMode('advanced')}>
            Nâng cao
          </Tag.CheckableTag>
        </div>

        {mode === 'simple' && (
          <div>
            <p style={{ fontSize: 12, color: '#5b6875' }}>
              Chọn 2 biến và phép toán. VD: <code>so_luong × don_gia</code>
            </p>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={opLeft || undefined}
                onChange={setOpLeft}
                options={availableVars.map(v => ({ value: v, label: v }))}
                placeholder="Biến trái"
                showSearch
                style={{ width: '40%' }}
              />
              <Select
                value={opType}
                onChange={setOpType}
                options={OPERATORS}
                style={{ width: '20%' }}
              />
              <Select
                value={opRight || undefined}
                onChange={setOpRight}
                options={availableVars.map(v => ({ value: v, label: v }))}
                placeholder="Biến phải"
                showSearch
                style={{ width: '40%' }}
              />
            </Space.Compact>
            <Button
              type="dashed"
              size="small"
              block
              style={{ marginTop: 8 }}
              icon={<Plus size={12} />}
              onClick={buildSimple}
              disabled={!opLeft || !opRight}
            >
              Sinh công thức
            </Button>
          </div>
        )}

        {mode === 'function' && (
          <div>
            <p style={{ fontSize: 12, color: '#5b6875' }}>
              Chọn hàm và 2 tham số. Hàm thường dùng nhất:{' '}
              <code>max(0, tong_cong - tam_ung)</code> — đảm bảo không âm.
            </p>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Select
                value={funcName}
                onChange={setFuncName}
                options={FUNCS}
                style={{ width: '100%' }}
              />
              <Input
                value={funcArg1}
                onChange={(e) => setFuncArg1(e.target.value)}
                placeholder="Tham số 1 (số hoặc tên biến hoặc biểu thức)"
                addonBefore="Arg 1:"
              />
              <Input
                value={funcArg2}
                onChange={(e) => setFuncArg2(e.target.value)}
                placeholder="Tham số 2"
                addonBefore="Arg 2:"
              />
              <p style={{ fontSize: 11, color: '#8a95a5', margin: 0 }}>
                Tham số có thể là biểu thức kết hợp. VD: <code>tong_cong - tam_ung</code>
              </p>
              <Button
                type="dashed"
                size="small"
                block
                icon={<Plus size={12} />}
                onClick={buildFunction}
                disabled={!funcArg1 || !funcArg2}
              >
                Sinh công thức
              </Button>
            </Space>
          </div>
        )}

        {mode === 'advanced' && (
          <div>
            <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message="Chỉ dành cho người am hiểu syntax."
              description={
                <span style={{ fontSize: 11 }}>
                  Hỗ trợ: + - * / ( ) và hàm max/min/abs/round/floor/ceil. Tên biến chỉ dùng a-z 0-9 _.
                </span>
              }
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Công thức kết quả:</div>
          <Input.TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
            placeholder="Trống = không tính tự động"
          />
        </div>

        {availableVars.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#8a95a5', marginBottom: 4 }}>
              Biến có sẵn (click để chèn):
            </div>
            <Space size={[4, 4]} wrap>
              {availableVars.map(v => (
                <Tooltip key={v} title={`Click để chèn vào công thức`}>
                  <Tag
                    style={{ cursor: 'pointer', margin: 0 }}
                    onClick={() => setDraft((prev) => prev + (prev && !prev.endsWith(' ') ? ' ' : '') + v)}
                  >
                    {v}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        )}
      </Modal>
    </>
  );
}
